import { Box, CircularProgress } from '@mui/material';
import BackendService from 'BackendService';
import { CommonLoggedInDataContext } from 'CommonLoggedInData';
import fundOpportunityContentCache from 'FundOpportunityContentCache';
import React from 'react';

interface AssetImageProps {
  url?: string;
  alt?: string;
  className?: string;
  printMode?: boolean;
  asCard?: boolean;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  sx?: any;
  onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLImageElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLImageElement>) => void;
}

interface AssetImageState {
  imageUrl: string | null;
  isLoading: boolean;
}

class AssetImage extends React.Component<AssetImageProps, AssetImageState> {
  static contextType = CommonLoggedInDataContext;

  constructor(props: AssetImageProps) {
    super(props);

    // Initialize state without URL parsing - will be handled in componentDidMount
    const initialState = {
      imageUrl: this.props.url?.match(/^\/api/) ? null : this.props.url || null,
      isLoading: this.props.url?.match(/^\/api/) ? true : false,
    };

    this.state = initialState;
  }

  componentDidMount() {
    this.loadImage();
  }

  componentDidUpdate(prevProps: AssetImageProps) {
    // React to URL changes by checking if printMode or url changed
    if (prevProps.url !== this.props.url || prevProps.printMode !== this.props.printMode) {
      this.loadImage();
    }
  }

  private getPrintModeFromUrl(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('printMode') === 'true';
  }

  private loadImage() {
    const { url, printMode } = this.props;

    // Check for printMode in URL parameters if not explicitly provided
    const printModeFromUrl = this.getPrintModeFromUrl();
    const isPrintMode = printMode || printModeFromUrl;

    // Update loading state based on current conditions
    const shouldLoad = url && (url.match(/^\/api/) || isPrintMode);
    const shouldShowLoading = url && (url.match(/^\/api/) || isPrintMode);

    if (shouldShowLoading) {
      this.setState({ isLoading: true });
    }

    if (shouldLoad) {
      if (isPrintMode) {
        // In print mode, get the presigned URL directly without blob conversion
        const path = url.match(/^\/api/) ? url.replace(/^\/api\//, '') : url;
        console.log('AssetImage: Getting presigned URL for path:', path, 'printMode:', isPrintMode);
        new BackendService()
          .getAssetPresignedURL(path)
          .then((presignedUrl) => {
            console.log('AssetImage: Got presigned URL:', presignedUrl);
            this.setState({ imageUrl: presignedUrl, isLoading: false });
          })
          .catch((error) => {
            console.error('AssetImage: Failed to get presigned URL:', error);
            this.setState({ isLoading: false });
          });
      } else {
        // Normal mode - use cached/preview URL
        fundOpportunityContentCache
          .getAssetImageUrl(url)
          .then((signedUrl: string) => {
            this.setState({ imageUrl: signedUrl as string, isLoading: false });
          })
          .catch(() => this.setState({ isLoading: false }));
      }
    }
  }

  render() {
    const { alt = '', className, onLoad, sx } = this.props;
    const { imageUrl, isLoading } = this.state;

    if (isLoading) {
      return (
        <Box
          className={`react-AssetImage ${className || ''}`.trim()}
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: sx?.width || '100%',
            height: sx?.height || '100%',
            ...sx,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (!imageUrl) {
      return null;
    }

    return (
      <Box
        component="img"
        src={imageUrl}
        alt={alt}
        onLoad={(e) => onLoad?.(e)}
        sx={sx}
        className={`asset-image-dynamic ${className || ''}`.trim()}
        onClick={(e) => this.props.onClick?.(e)}
        onMouseEnter={(e) => this.props.onMouseEnter?.(e)}
        onMouseLeave={(e) => this.props.onMouseLeave?.(e)}
      />
    );
  }
}

export default AssetImage;
